/** Real client for the web chat block routes (same origin + session cookie) — api/app/webchat/block_routes.py. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type { BlockBody, WcBlockApi, WebBlockList, WebBlockState } from './types'

export class HttpWcBlockApi implements WcBlockApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const res = await this.fetchImpl(new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin).toString(), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let message = res.status === 403 ? 'ไม่มีสิทธิ์ทำรายการนี้' : `HTTP ${res.status}`
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') message = j.detail
        } catch {
          /* not JSON */
        }
        throw new ApiError(res.status, message)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'ระบบไม่ตอบในเวลาที่กำหนด ลองใหม่อีกครั้ง')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง')
    } finally {
      clearTimeout(timer)
    }
  }

  async getState(convId: string): Promise<WebBlockState | null> {
    try {
      return await this.req<WebBlockState>('GET', `/conversations/${encodeURIComponent(convId)}/web/block`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }

  block(convId: string, body: BlockBody): Promise<WebBlockState> {
    return this.req('POST', `/conversations/${encodeURIComponent(convId)}/web/block`, body)
  }

  unblock(convId: string, note: string | null = null): Promise<WebBlockState> {
    return this.req('POST', `/conversations/${encodeURIComponent(convId)}/web/unblock`, { note })
  }

  list(): Promise<WebBlockList> {
    return this.req('GET', '/webchat/blocks')
  }

  unblockById(blockId: string, note: string | null = null): Promise<WebBlockList> {
    return this.req('POST', `/webchat/blocks/${encodeURIComponent(blockId)}/unblock`, { note })
  }
}
